import { action, computed, observable } from "mobx";

import { ACCENTS } from "../theme/accents";
import type { AccentName } from "../theme/accents";
import { buildTheme } from "../theme/buildTheme";
import type { ChordColor, ThemeBase } from "../theme/buildTheme";
import type { Theme } from "../theme/themes";
import { loadSettings, saveSettings } from "./savedSettings";

const KEY = "bandstand.theme";

/** Light, dark or black, or following the system between light and dark. */
export type ThemeMode = ThemeBase | "system";

const DEFAULTS = { mode: "system" as ThemeMode, accent: "blue" as AccentName, chords: "accent" as ChordColor };

const MODES: ThemeMode[] = ["system", "light", "dark", "black"];

/** The colours of the app, as chosen in the appearance settings. They're kept on this device. */
export class ThemeStore {
    @observable accessor mode = DEFAULTS.mode;

    @observable accessor accent = DEFAULTS.accent;

    @observable accessor chords = DEFAULTS.chords;

    /** Whether the system is set to dark, for the system mode. */
    @observable accessor systemDark = false;

    constructor() {
        this.take(loadSettings(KEY, DEFAULTS));

        if (typeof window === "undefined" || !window.matchMedia) return;

        const query = window.matchMedia("(prefers-color-scheme: dark)");

        this.setSystemDark(query.matches);
        query.addEventListener("change", (event) => this.setSystemDark(event.matches));
    }

    /** What's kept, also on the share server. */
    @computed get saved(): typeof DEFAULTS {
        return { mode: this.mode, accent: this.accent, chords: this.chords };
    }

    @computed get base(): ThemeBase {
        if (this.mode !== "system") return this.mode;

        return this.systemDark ? "dark" : "light";
    }

    @computed get colors(): Theme {
        return buildTheme(this.base, this.accent, this.chords);
    }

    @action
    setMode(mode: ThemeMode): void {
        this.mode = mode;
        this.save();
    }

    @action
    setAccent(accent: AccentName): void {
        this.accent = accent;
        this.save();
    }

    @action
    setChords(chords: ChordColor): void {
        this.chords = chords;
        this.save();
    }

    /** Takes settings from elsewhere, like the share server. Names this app doesn't know are left as they are. */
    @action
    apply(saved: { mode: string; accent: string; chords: string }): void {
        this.take(saved);
        this.save();
    }

    @action
    reset(): void {
        Object.assign(this, DEFAULTS);
        this.save();
    }

    @action
    private take({ mode, accent, chords }: { mode: string; accent: string; chords: string }): void {
        if (MODES.includes(mode as ThemeMode)) this.mode = mode as ThemeMode;
        if (accent in ACCENTS) this.accent = accent as AccentName;
        if (chords === "accent" || chords === "text" || chords in ACCENTS) this.chords = chords as ChordColor;
    }

    @action
    private setSystemDark(dark: boolean): void {
        this.systemDark = dark;
    }

    private save(): void {
        saveSettings(KEY, this.saved);
    }
}
